import { datadogRum } from "@datadog/browser-rum";
import useStore from "stores/index";
import { ESS_OPEN, ESS_CLOSED, ESS_CONNECTING, ESS_CONNECT_FATAL } from "stores/eventServiceStatusStore";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import * as api from "../api/api";
import * as eventApi from "../api/eventApi";
import { retryAsync } from "../utils/retryAsync";

type EventServiceStatus = typeof ESS_OPEN | typeof ESS_CLOSED | typeof ESS_CONNECTING | typeof ESS_CONNECT_FATAL;

const SSE_SUBSCRIBED_EVENTS = ["MESSAGE", "SCREENSHOT_UPDATE", "CLASS_STARTED", "STUDENT_CLAIM_UPDATE"] as const;

const EVENT_SERVICE_RETRY = { attempts: 5, delayMs: 1000 } as const;
const MAX_BACKOFF_SEC = 64;

type EventSourceStatusSetter = (status: EventServiceStatus) => void;

function getEventServiceStatusSetter(): EventSourceStatusSetter {
	const state = useStore.getState() as unknown as {
		eventServiceStatus: { setStatus: EventSourceStatusSetter };
	};
	return state.eventServiceStatus.setStatus;
}

export type EventStreamClient = {
	/** Live SSE connection, or `null` when disconnected. Writable so tests can inject a mock `EventSource`. */
	eventSource: EventSource | null;
	connect(): Promise<void>;
	disconnect(retrying?: boolean): void;
	/** For `useSyncExternalStore` when React UI must re-bind when the `EventSource` instance changes. */
	subscribeToEventSource(onChange: () => void): () => void;
};

function buildEventStreamUrl(baseUrl: string, deviceId: string, username: string): string {
	const events = SSE_SUBSCRIBED_EVENTS.join(",");
	return `${baseUrl}/events/v2/appliance/${encodeURIComponent(deviceId)}/recipient/${encodeURIComponent(username)}?events=${events}`;
}

/**
 * SSE client for the appliance event service. One shared instance per app session (see `eventService` default export).
 */
export function createEventStreamClient(): EventStreamClient {
	const stream: EventStreamClient = {
		eventSource: null,
		connect: async () => {},
		disconnect: () => {},
		subscribeToEventSource: () => () => {},
	};

	let apiDispUrl: string | null = null;
	let retryBackoffSecs = 1;
	let retriedWithAuth = false;
	const eventSourceListeners = new Set<() => void>();

	const notifyEventSourceSubscribers = (): void => {
		for (const listener of eventSourceListeners) {
			listener();
		}
	};

	const setStatus = (status: EventServiceStatus): void => {
		getEventServiceStatusSetter()(status);
	};

	const disconnect = (retrying?: boolean): void => {
		if (stream.eventSource) {
			stream.eventSource.close();
			if (!retrying) {
				retriedWithAuth = false;
				setStatus(ESS_CLOSED);
			}
		}
		stream.eventSource = null;
		notifyEventSourceSubscribers();
	};

	const retryConnectWithAuth = async (): Promise<void> => {
		const onAuthFailureHandler = (response: Response): void => {
			if (response.status === 401 || response.status === 403) {
				disconnect();
				useUserSessionStore.getState().handleInvalidTokenError(new Error("Session is no longer valid"));
			}

			return;
		};

		try {
			const fetchedApiDispUrl = (await api.get("/api-url", {}, true, onAuthFailureHandler)) as { result: string };

			apiDispUrl = fetchedApiDispUrl.result;

			await api.fetch(
				`${apiDispUrl}/v2/authenticate/set-cookie`,
				{
					method: "POST",
					mode: "no-cors",
					credentials: "include",
					body: JSON.stringify({
						access_token: localStorage.getItem("TOKEN"),
					}),
				},
				false,
				() => onAuthFailureHandler
			);
		} catch (error) {
			console.error(
				"Failed to authenticate before retrying to connect to event-service, triggering connect retry again after timeout",
				error
			);
			datadogRum.addError(error instanceof Error ? error : new Error(String(error)));
			retryBackoffSecs = Math.min(MAX_BACKOFF_SEC, retryBackoffSecs * 2);
			setTimeout(() => {
				void connect();
			}, retryBackoffSecs * 1000);
			return;
		}

		retriedWithAuth = true;
		void connect();
	};

	const connect = async (): Promise<void> => {
		if (stream.eventSource) {
			return;
		}

		const username = localStorage.getItem("USERNAME");
		const deviceId = localStorage.getItem("DEVICE");
		if (!username || !deviceId) {
			console.warn("Event stream connect skipped: missing USERNAME or DEVICE in localStorage");
			return;
		}

		let resolvedBaseUrl: string;
		try {
			setStatus(ESS_CONNECTING);
			resolvedBaseUrl = await retryAsync(EVENT_SERVICE_RETRY.attempts, EVENT_SERVICE_RETRY.delayMs, eventApi.getEventServiceUrl);
		} catch (error) {
			setStatus(ESS_CONNECT_FATAL);
			console.error("Failed to fetch event service or api-disp URL");
			throw error;
		}

		const source = new EventSource(buildEventStreamUrl(resolvedBaseUrl, deviceId, username), { withCredentials: true });
		stream.eventSource = source;
		notifyEventSourceSubscribers();
		setStatus(ESS_CONNECTING);

		source.onerror = () => {
			setStatus(ESS_CONNECTING);
			disconnect(true);

			if (!retriedWithAuth) {
				void retryConnectWithAuth();
				return;
			}

			setTimeout(() => {
				void connect();
			}, retryBackoffSecs * 1000);
			retryBackoffSecs = Math.min(MAX_BACKOFF_SEC, retryBackoffSecs * 2);
		};

		source.onopen = () => {
			retriedWithAuth = false;
			setStatus(ESS_OPEN);
		};
	};

	const subscribeToEventSource = (onChange: () => void): (() => void) => {
		eventSourceListeners.add(onChange);
		return () => {
			eventSourceListeners.delete(onChange);
		};
	};

	stream.connect = connect;
	stream.disconnect = disconnect;
	stream.subscribeToEventSource = subscribeToEventSource;

	return stream;
}

export const eventStreamClient = createEventStreamClient();
